import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FetchLicensedDirectorBillingDetailsResponseTutors } from '../../../../../swagger'
import getLocaleCurrencyForAmount from '../../../../../utils/getLocaleCurrencyForAmount'
import ContainedButton, {
  ContainedButtonVariant,
} from '../../../../Buttons/ContainedButton'
import LicensingPaymentModal from '../../../../Modals/LicensingPaymentModal'
import { TutorLicensingDetails } from './TutorLicensingDetails'
import {
  convertTwoDecimalStringToBig,
  formatBigFixedTwoDecimals,
} from '../../../../../utils/bigJSHelper'
import { useNavigate } from 'react-router'
import OutlinedButton, {
  OutlinedButtonVariant,
} from '@components/Buttons/OutlinedButton'

interface LCDLicensingBreakdownTutorProps {
  tutors: FetchLicensedDirectorBillingDetailsResponseTutors[]
  programName: string
  programKey: number
  communityName: string
  directorName: string
  onPaymentComplete?: () => void
}

const HeaderCell = styled(TableCell)(({ theme }) => ({
  width: '25%',
  padding: 0,
  [theme.breakpoints.down('md')]: {
    width: '40%',
  },
}))

const HeaderText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.25rem',
  width: '60%',
  [theme.breakpoints.down('md')]: {
    width: '70%',
  },
}))

const AmountText = styled(Typography)({
  width: '50%',
  textAlign: 'end',
})

const TutorNameBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
})

const ExpandIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s',
}))

const TutorTableRow = styled(TableRow)({
  height: 50,
})

const TutorTableCell = styled(TableCell)({
  width: '25%',
  padding: 0,
})

const CollapsibleCell = styled(TableCell)({
  padding: 0,
})

const DetailsBox = styled(Box)({
  marginLeft: 0,
  marginRight: 0,
  marginBottom: 16,
})

const LCDLicensingBreakdownTutor: React.FC<LCDLicensingBreakdownTutorProps> = ({
  tutors,
  programName,
  programKey,
  communityName,
  directorName,
  onPaymentComplete,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [selectedTutor, setSelectedTutor] =
    useState<FetchLicensedDirectorBillingDetailsResponseTutors | null>(null)

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index))
  }

  return (
    <TableContainer sx={{ marginTop: 6 }}>
      {selectedTutor && (
        <LicensingPaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          titleInfo={{
            programName,
            communityName,
            directorName,
          }}
          subHeaderLabel={t(
            'LCDLicensingBreakdownTutor.LicensingPaymentModal.SubHeader.Label',
            'Tutor {{name}}',
            {
              name: `${selectedTutor.firstName} ${selectedTutor.lastName}`,
            }
          )}
          programKey={programKey}
          userKey={selectedTutor?.userKey as number}
          actorKey={selectedTutor?.actorKey}
          onPaymentComplete={onPaymentComplete}
        />
      )}

      <Table aria-label="tutor billing table">
        <TableHead>
          <TutorTableRow>
            <HeaderCell>
              <HeaderText>{t('Tutor', 'Tutor')}</HeaderText>
            </HeaderCell>
            <HeaderCell>
              <HeaderText
                sx={{
                  textAlign: 'end',
                }}
              >
                {t('Balance', 'Balance To Director')}
              </HeaderText>
            </HeaderCell>
            <TutorTableCell />
            <TutorTableCell />
          </TutorTableRow>
        </TableHead>
        <TableBody>
          {tutors.map((tutor, index) => {
            const balance = convertTwoDecimalStringToBig(
              tutor.totalOwed.amount
            ).minus(tutor.totalPaid.amount)
            const isAccordionExpanded = openIndex === index
            return (
              <Fragment key={tutor.firstName + index}>
                <TutorTableRow>
                  <TutorTableCell
                    component="th"
                    scope="row"
                    sx={{ padding: 0 }}
                  >
                    <TutorNameBox>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {tutor.firstName} {tutor.lastName}
                      </Typography>
                      <IconButton
                        onClick={() => handleToggle(index)}
                        size="small"
                        sx={{ ml: 1 }}
                      >
                        <ExpandIcon expanded={isAccordionExpanded} />
                      </IconButton>
                    </TutorNameBox>
                  </TutorTableCell>

                  <TutorTableCell sx={{ paddingLeft: 0 }}>
                    {!isAccordionExpanded && (
                      <AmountText variant="body1">
                        {getLocaleCurrencyForAmount(
                          formatBigFixedTwoDecimals(balance),
                          tutor.totalPaid.currencyCode
                        )}
                      </AmountText>
                    )}
                  </TutorTableCell>

                  {isAccordionExpanded && tutor.userKey ? (
                    <>
                      <TutorTableCell align="right">
                        <OutlinedButton
                          id="viewTutorProfile"
                          variant={OutlinedButtonVariant.ViewTutorProfile}
                          onClick={() => {
                            navigate(`/admin/user-accounts/${tutor.userKey}`)
                          }}
                        />
                      </TutorTableCell>
                      <TutorTableCell align="right">
                        <ContainedButton
                          id="record-tutor-payment"
                          variant={
                            ContainedButtonVariant.RecordTutorTransaction
                          }
                          onClick={() => {
                            setSelectedTutor(tutor)
                            setIsPaymentModalOpen(true)
                          }}
                        />
                      </TutorTableCell>
                    </>
                  ) : (
                    <TutorTableCell />
                  )}
                </TutorTableRow>

                <TableRow>
                  <CollapsibleCell colSpan={4}>
                    <Collapse
                      in={openIndex === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <DetailsBox>
                        <TutorLicensingDetails
                          tutor={tutor}
                          programName={programName}
                        />
                      </DetailsBox>
                    </Collapse>
                  </CollapsibleCell>
                </TableRow>
              </Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default LCDLicensingBreakdownTutor
